import { produce } from "immer";
import { clone, mergeAll, omit, uniqBy } from "ramda";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import {
    OpenAPI,
    SubscribableAssetsService,
    SubscriptionSharingService,
    UserDetailsService,
    type SubscribableAsset,
} from "@hsl/lgim-explorer/src/openapi";

import {
    createFundWatchData,
    createNotificationQItem,
    decodeToken,
    getSubAssetFundId,
    getUserInviteIds,
    getUserType,
    getWelcomeNotificationType,
    newSubscriptions,
} from "./lib";
import {
    fetchSubscriptions,
    fetchUser,
    getSubscriptionUpdateData,
    obtainToken,
} from "./services";
import type { CreateNotification, FlowSteps, Store, User } from "./types";

const defaultFlowSteps: FlowSteps = {
    "select-funds": {
        complete: true,
        name: "select-funds",
        incompleteMsg: "No funds selected",
    },
    "select-documents": {
        complete: false,
        name: "select-documents",
        incompleteMsg: "No documents selected",
    },
    "add-details": {
        complete: false,
        name: "add-details",
        incompleteMsg: "No user details provided",
    },
    "add-recipient": {
        complete: false,
        name: "add-recipient",
        incompleteMsg: "No recipients added",
    },
    "verify-email": { complete: false, name: "verify-email" },
};

const useFCSubscriptionStore = create<Store>()(
    devtools(
        persist(
            (set, get) =>
                ({
                    token: null,
                    dach: false,
                    loadingUser: true,
                    latestFC: "/",
                    setLatestFC(fc) {
                        set({ latestFC: fc });
                    },
                    user: null,
                    userSubscriptions: [],
                    selectionBasketMode: "watch",
                    selectionBasket: {
                        funds: [],
                        shareClasses: [],
                    },
                    flowSteps: clone<FlowSteps>(defaultFlowSteps),
                    retrieveFunds: false,
                    pending: [],
                    notificationQueue: [],
                    myFundsSort: "most recent",
                    myFundsFilter: "all",
                    sharingMode: "none",
                    updates: {},
                    setMyFundsSort(sort) {
                        set({ myFundsSort: sort }, false, {
                            type: `Sort my funds by ${sort}`,
                        });
                    },
                    setMyFundsFilter(filter) {
                        set({ myFundsFilter: filter }, false, {
                            type: `Filter my funds by ${filter}`,
                        });
                    },
                    setPendingSubscriptions(data) {
                        set({ pending: [...get().pending, ...data] }, false, {
                            type: "Set pending subscriptions",
                        });
                    },
                    resetPendingSubscriptions() {
                        set({ pending: [] }, false, {
                            type: "Reset pending subscriptions",
                        });
                    },
                    setRetrieveFunds(retrieve) {
                        set({ retrieveFunds: retrieve }, false, {
                            type: "Set retrieve funds",
                        });
                    },
                    async init(props) {
                        if (props?.OpenAPIBase) {
                            OpenAPI.BASE = props.OpenAPIBase;
                        }
                        const { userId, inviteId, salesforceToken } =
                            getUserInviteIds();
                        const {
                            token: _token,
                            clearPendingSubscriptions,
                            addNotification,
                            pending,
                            sharingMode,
                        } = get();
                        const userType = getUserType(
                            userId,
                            inviteId,
                            salesforceToken ?? _token,
                        );
                        const { token, dach } = await obtainToken(
                            userId,
                            inviteId,
                            salesforceToken ?? _token,
                        );
                        if (dach) {
                            // DACH region - force user to fill out form
                            return set({ dach, loadingUser: false }, false, {
                                type: "Show DACH form",
                            });
                        }
                        const decoded = decodeToken(token);
                        if (decoded && token) {
                            OpenAPI.TOKEN = token;
                            const user = await fetchUser(decoded, userType);
                            if (user && sharingMode === "none") {
                                await clearPendingSubscriptions(user.userId);
                            }
                            const userSubscriptions =
                                await fetchSubscriptions(decoded);
                            const subscriptions =
                                userType === "new"
                                    ? userSubscriptions
                                    : pending;
                            const welcomeNotification =
                                userType !== "existing" &&
                                sharingMode === "none"
                                    ? getWelcomeNotificationType(
                                          userType,
                                          subscriptions,
                                      )
                                    : null;
                            if (welcomeNotification) {
                                addNotification(welcomeNotification, {
                                    count: new Set(
                                        subscriptions.map((x) => x.fundId),
                                    ).size,
                                });
                            }

                            const updates = user
                                ? await getSubscriptionUpdateData(
                                      user,
                                      userSubscriptions,
                                  )
                                : {};
                            set(
                                {
                                    user,
                                    userSubscriptions,
                                    token: user ? token : null,
                                    loadingUser: false,
                                    updates,
                                },
                                false,
                                { type: "Init user data" },
                            );
                            if (
                                user &&
                                userType !== "existing" &&
                                sharingMode !== "none" &&
                                props &&
                                props.navigate
                            ) {
                                props.navigate(
                                    `/my-funds/${sharingMode}/share`,
                                );
                            }
                        } else {
                            OpenAPI.TOKEN = undefined;
                            set({ loadingUser: false }, false, {
                                type: "Finish loading user data",
                            });
                        }
                    },
                    setBasketMode(mode) {
                        set({ selectionBasketMode: mode }, false, {
                            type: "Set basket mode",
                        });
                    },
                    toggleBasketItem(fund, scs = []) {
                        set(
                            produce<Store>((state) => {
                                const basketFunds = state.selectionBasket.funds;
                                const basketSCs =
                                    state.selectionBasket.shareClasses;
                                const remove = basketFunds.some(
                                    (f) => f.data.id === fund.data.id,
                                );
                                const funds = remove
                                    ? basketFunds.filter(
                                          (f) => f.data.id !== fund.data.id,
                                      )
                                    : [...basketFunds, fund];
                                const shareClasses = remove
                                    ? basketSCs.filter(
                                          (sc) =>
                                              !fund.data.share_classes.includes(
                                                  sc.data.id,
                                              ),
                                      )
                                    : [...basketSCs, ...scs];
                                const basket = {
                                    funds,
                                    shareClasses,
                                    type:
                                        get().selectionBasketMode ===
                                        "subscribe"
                                            ? ("FULL" as const)
                                            : ("WATCHLIST" as const),
                                };
                                state.selectionBasket = basket;
                                state.flowSteps["select-funds"].complete =
                                    basket.funds.length > 0;
                                state.flowSteps["select-funds"].data = basket;
                            }),
                            false,
                            {
                                type: `Toggle basket item: ${fund.data.localId}`,
                            },
                        );
                    },
                    clearBasket() {
                        set(
                            {
                                selectionBasket: {
                                    funds: [],
                                    shareClasses: [],
                                },
                            },
                            false,
                            {
                                type: "Clear basket",
                            },
                        );
                    },
                    getFlowStepData(step) {
                        return get().flowSteps[step];
                    },
                    setFlowStepData(name, data) {
                        set(
                            produce<Store>((state) => {
                                state.flowSteps[name] = Object.assign(
                                    {},
                                    state.flowSteps[name],
                                    data,
                                );
                            }),
                            false,
                            { type: `Set ${name} step data.` },
                        );
                    },
                    resetFlow() {
                        set(
                            produce<Store>((state) => {
                                state.flowSteps = mergeAll([
                                    state.flowSteps,
                                    omit(["select-funds"], defaultFlowSteps),
                                ]);
                            }),
                            false,
                            {
                                type: "Reset flow",
                            },
                        );
                    },
                    async refreshUserSubscriptions() {
                        const token = get().token;
                        const user = get().user;
                        if (!token || !user) throw new Error("Not logged in");
                        const subscriptions =
                            await SubscribableAssetsService.getSubscriptions(
                                user.userId,
                            );
                        set(
                            {
                                userSubscriptions:
                                    subscriptions as Required<SubscribableAsset>[],
                            },
                            false,
                            { type: "Refresh user subscriptions" },
                        );
                    },
                    getSubscribedFunds(filter = () => true) {
                        const userSubscriptions = get().userSubscriptions;
                        return Array.from(
                            new Set(
                                userSubscriptions
                                    .filter(filter)
                                    .map((s) => s.fundId),
                            ),
                        );
                    },
                    async clearPendingSubscriptions(userId) {
                        try {
                            const { pending, resetPendingSubscriptions } =
                                get();
                            if (pending.length > 0) {
                                if (!userId) {
                                    throw new Error("No user id");
                                }
                                await SubscribableAssetsService.createSubscriptions(
                                    userId,
                                    pending,
                                );
                                resetPendingSubscriptions();
                            }
                        } catch (err) {
                            console.error(
                                "Error creating pending subscriptions:",
                                err,
                            );
                        }
                    },
                    addNotification(type, context) {
                        const qItem = createNotificationQItem(type);
                        get().setNotificationQueue({ ...qItem, context });
                    },
                    setNotificationQueue(item, remove) {
                        const items =
                            !item || Array.isArray(item) ? item : [item];
                        const state = get().notificationQueue;
                        const reset = !items || items.length === 0;
                        const notificationQueue = reset
                            ? []
                            : remove
                              ? state.filter(
                                    (item) =>
                                        !items.some(
                                            (itm) => itm.id === item.id,
                                        ),
                                )
                              : [...state, ...items];
                        set(
                            {
                                notificationQueue,
                            },
                            false,
                            {
                                type: "Update notification queue",
                            },
                        );
                    },
                    createNotificationContext(item) {
                        const { getSubscribedFunds, selectionBasket } = get();
                        const { context } = item;
                        const funds = getSubscribedFunds();
                        switch (item.type) {
                            case "WMF":
                            case "WBMF":
                            case "WB":
                            case "WMF_WATCH":
                            case "WB_WATCH":
                            case "UPDATES":
                            case "UPDATES_LINK":
                                return {
                                    ...item,
                                    count: context?.count ?? funds.length,
                                } as Extract<
                                    CreateNotification,
                                    { type: "WB" }
                                >;
                            case "WMF_SHARE":
                            case "WMF_SHARE_LINK":
                            case "WB_SHARE":
                            case "WB_SHARE_LINK":
                            case "SHARE_LINK":
                                return {
                                    ...item,
                                    count: context?.count ?? funds.length,
                                    email:
                                        context?.email ??
                                        "philipp.koenigsmarck@lgim.com",
                                } as Extract<
                                    CreateNotification,
                                    { type: "WMF_SHARE" }
                                >;
                            case "W_SHARE_WITH":
                            case "WB_SHARE_WITH":
                            case "SHARE_WITH":
                                return {
                                    ...item,
                                    count: context?.count ?? funds.length,
                                    emails: context?.emails ?? [
                                        "michael.marks@lgim.com",
                                        "hanna.holstrom@lgim.com",
                                    ],
                                } as Extract<
                                    CreateNotification,
                                    { type: "W_SHARE_WITH" }
                                >;
                            case "GNSF":
                                return item as Extract<
                                    CreateNotification,
                                    { type: "GNSF" }
                                >;
                            case "NFS":
                            case "NFA_WATCH":
                                return {
                                    ...item,
                                    count:
                                        context?.count ??
                                        selectionBasket.funds.length,
                                } as Extract<
                                    CreateNotification,
                                    { type: "NFA_WATCH" }
                                >;
                        }
                    },
                    closeDACHScreen() {
                        set({ dach: false }, false, {
                            type: "Close DACH form",
                        });
                    },
                    async watchFunds(funds) {
                        const {
                            user,
                            addNotification,
                            clearBasket,
                            refreshUserSubscriptions,
                            resetPendingSubscriptions,
                            userSubscriptions,
                        } = get();
                        if (!user || !user.userId) {
                            return;
                        }
                        const subscribable = newSubscriptions(
                            userSubscriptions,
                            funds.map(createFundWatchData),
                        );
                        if (subscribable.length > 0) {
                            await SubscribableAssetsService.createSubscriptions(
                                user.userId,
                                subscribable,
                            );
                            await refreshUserSubscriptions();
                            addNotification("NFA_WATCH", {
                                count: funds.length,
                            });
                        }
                        clearBasket();
                        resetPendingSubscriptions();
                    },
                    async subscribeTo(subscriptions, notify = true) {
                        const {
                            user,
                            refreshUserSubscriptions,
                            resetPendingSubscriptions,
                            addNotification,
                            clearBasket,
                            userSubscriptions,
                        } = get();
                        if (!user) throw new Error("User is not logged in");
                        const subscribable = newSubscriptions(
                            userSubscriptions,
                            subscriptions,
                        );
                        const fundsCount = uniqBy<
                            SubscribableAsset,
                            string | undefined
                        >((s) => s.fundId, subscribable).length;
                        if (subscribable.length > 0) {
                            await SubscribableAssetsService.createSubscriptions(
                                user.userId,
                                subscribable,
                            );
                            notify &&
                                addNotification("NFS", {
                                    count: fundsCount,
                                });
                            await refreshUserSubscriptions();
                        }
                        clearBasket();
                        resetPendingSubscriptions();
                    },
                    async updateUser(data) {
                        try {
                            const { user } = get();
                            if (!user || !user.userId) {
                                return;
                            }
                            await UserDetailsService.updateUser(user.userId, {
                                title: user.title,
                                company: user.company,
                                firstName: user.firstName,
                                lastName: user.lastName,
                                jobTitle: user.jobTitle,
                                language: user.language,
                                dayOfWeek: user.dayOfWeek,
                                profile: user.profile,
                                ...data,
                            });
                            const newUserDetails =
                                await UserDetailsService.getUser(user.userId);
                            set({ user: newUserDetails as User }, false, {
                                type: "Update user",
                            });
                        } catch (err) {
                            console.error("Error updating user:", err);
                            throw new Error("Error updating user");
                        }
                    },
                    setSharingMode(sharingMode) {
                        set({ sharingMode }, false, {
                            type: `Set sharing mode: ${sharingMode}`,
                        });
                    },
                    resetSharingMode() {
                        get().setSharingMode("none");
                    },
                    async shareSubscriptions(shareObjects, notify = true) {
                        const {
                            user,
                            resetSharingMode,
                            clearBasket,
                            addNotification,
                            resetPendingSubscriptions,
                        } = get();
                        if (!user || !user.userId)
                            throw new Error("Not logged in");
                        await Promise.all(
                            shareObjects.map(async (share) => {
                                await SubscriptionSharingService.shareSubscriptions(
                                    user.userId,
                                    share,
                                );
                            }),
                        );
                        clearBasket();
                        resetSharingMode();
                        resetPendingSubscriptions();
                        if (notify) {
                            addNotification("SHARE_WITH", {
                                emails: shareObjects.map(
                                    (s) => s.recipientEmail,
                                ),
                            });
                        }
                    },
                    markRead(id) {
                        set(
                            produce<Store>((state) => {
                                if (!id) {
                                    state.updates = {};
                                } else if (state.isUpdated(id)) {
                                    if (id in state.updates) {
                                        delete state.updates[id];
                                    } else {
                                        const [fundId, docIds] =
                                            Object.entries(state.updates).find(
                                                ([_, docIds]) =>
                                                    docIds.includes(id),
                                            ) ?? [];

                                        if (fundId && docIds) {
                                            const unreadDocIds = docIds.filter(
                                                (dId) => dId !== id,
                                            );
                                            if (unreadDocIds.length) {
                                                state.updates[fundId] =
                                                    unreadDocIds;
                                            } else {
                                                delete state.updates[fundId];
                                            }
                                        }
                                    }
                                }
                            }),
                            false,
                            { type: `Mark document ${id ?? "all"} read` },
                        );
                    },
                    isUpdated(id) {
                        const { updates } = get();
                        return (
                            id in updates ||
                            Object.values(updates).flat().includes(id)
                        );
                    },
                    isUpdatedSubAsset(sub) {
                        const fundId = getSubAssetFundId(sub);
                        return fundId ? get().isUpdated(fundId) : false;
                    },
                    logOut() {
                        set(
                            { token: null, user: null, userSubscriptions: [] },
                            false,
                            {
                                type: "Log out",
                            },
                        );
                    },
                }) as Store,
            {
                name: "fc-subscription",
                partialize: omit([
                    // "flowSteps",
                    "user",
                    "userSubscriptions",
                    "loadingUser",
                    "notificationQueue",
                    "retrieveFunds",
                    "dach",
                    "myFundsFilter",
                    "myFundsSort",
                ]),
            },
        ),
        { name: "FC-SUBSCRIPTION" },
    ),
);

export default useFCSubscriptionStore;
